/**
 * PDP "Select Size" gate — goatusa.com parity.
 *
 * Live (src/js/bundles/snippets/variant-selection.js) starts with `isSizeOptionSelected = false`
 * whenever the product has a size option, and until the shopper picks one:
 *   - the CTA stays ENABLED but reads "Select Size" (main and sticky bar alike),
 *   - clicking the main CTA does nothing at all — no add, no animation,
 *   - clicking the STICKY CTA scrolls up to the variant rows,
 *   - no size chip is highlighted.
 * Choosing a size flips the label to "Add to Bag" and restores normal add-to-cart.
 *
 * Two mechanics make this reliable in Horizon, and both were wrong in the first attempt:
 *
 * 1. SCOPE. The variant picker is a SIBLING block of <product-form-component>, not a descendant,
 *    so scoping lookups to the form found zero size fieldsets and the gate silently no-oped.
 *    Everything is scoped to the buybox column instead.
 *
 * 2. INTERCEPT POINT. Horizon delegates every `on:click="..."` from `document` with
 *    `{ capture: true }` (assets/component.js), which is the first step of the propagation path.
 *    A capture listener on the form therefore runs AFTER it — the add already fired, which is why
 *    the fly-to-cart and checkmark still played. Listening on `window` in capture beats any
 *    document listener regardless of registration order, so that is where the block lives.
 *
 * FAIL-OPEN by design: no size option, a form inside a drawer, or any throw leaves the normal
 * add-to-cart completely intact. Never permanently blocks a purchase.
 */
(() => {
  const SELECT_SIZE = 'Select Size';
  const ADD_TO_BAG = 'Add to Bag';

  const isSizeFieldset = (fs) => {
    if (!fs) return false;
    const input = fs.querySelector('input[data-gtm-option-name]');
    const name = (
      input?.getAttribute('data-gtm-option-name') ||
      fs.querySelector('legend')?.textContent ||
      ''
    ).toLowerCase();
    return name.includes('size');
  };

  const scopeOf = (form) => form.closest('.product-details') || form.closest('.product-information');

  // A one-value size (One Size Fits All) has nothing to choose, so it stays preselected.
  const sizeFieldsets = (scope) =>
    Array.from(scope.querySelectorAll('fieldset.variant-option--buttons')).filter(
      (fs) => isSizeFieldset(fs) && fs.querySelectorAll('input[type="radio"]').length > 1
    );

  const mainButton = (form) => form.querySelector('[ref="addToCartButton"]');
  const mainLabel = (btn) => btn && btn.querySelector('.add-to-cart-text__content span span');
  const stickyButton = () => document.querySelector('.sticky-add-to-cart__button');
  const stickyLabel = () =>
    document.querySelector('.sticky-add-to-cart__button .add-to-cart-text__content span');

  const setText = (el, text) => {
    if (el && el.textContent.trim() !== text) el.textContent = text;
  };

  /** Every form currently gated and still awaiting a size. */
  const pendingForms = new Set();

  const applyPending = (form, scope) => {
    if (form.dataset.sizeChosen === 'true') return;
    const btn = mainButton(form);
    const fieldsets = sizeFieldsets(scope);
    if (!btn || !fieldsets.length) return;

    pendingForms.add(form);
    btn.setAttribute('data-size-pending', 'true');
    setText(mainLabel(btn), SELECT_SIZE);
    for (const fs of fieldsets) fs.setAttribute('data-size-pending', 'true');
    setText(stickyLabel(), SELECT_SIZE);
  };

  const clearPending = (form, scope) => {
    if (form.dataset.sizeChosen === 'true') return;
    form.dataset.sizeChosen = 'true';
    pendingForms.delete(form);

    const btn = mainButton(form);
    btn?.removeAttribute('data-size-pending');
    for (const el of scope.querySelectorAll('[data-size-pending]')) {
      el.removeAttribute('data-size-pending');
    }
    // Flip BOTH labels immediately, like live does (variant-selection.js sets textContent right
    // in the change handler). Waiting for the variant re-render to restore the main label left
    // "Select Size" on screen for the whole morph round-trip — seconds on the dev proxy — with
    // the gate already cleared. The morph then re-renders the server truth (e.g. "Sold Out")
    // over this optimistic flip, exactly as live's availability pass corrects its own.
    const main = mainLabel(btn);
    if (main && main.textContent.trim() === SELECT_SIZE) setText(main, ADD_TO_BAG);
    const sticky = stickyLabel();
    if (sticky && sticky.textContent.trim() === SELECT_SIZE) setText(sticky, ADD_TO_BAG);
  };

  /**
   * Blocks the add while a size is still pending. On `window` capture so it runs before Horizon's
   * document-capture delegation — see the note at the top.
   */
  window.addEventListener(
    'click',
    (event) => {
      if (!pendingForms.size) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const btn = target.closest('[ref="addToCartButton"], .sticky-add-to-cart__button');
      if (!btn) return;

      // Only block while this specific buybox is still pending.
      const stillPending = [...pendingForms].some((form) => {
        const scope = scopeOf(form);
        return scope && (scope.contains(btn) || btn.closest('.sticky-add-to-cart'));
      });
      if (!stillPending) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      // Live: the sticky CTA scrolls back to the variant rows; the main CTA does nothing.
      if (btn.closest('.sticky-add-to-cart')) {
        const form = [...pendingForms][0];
        const scope = form && scopeOf(form);
        sizeFieldsets(scope || document)[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    true
  );

  const initForm = (form) => {
    if (!(form instanceof HTMLElement) || form.dataset.sizeGate === 'on') return;
    // Quick-add / quick-shop drawers render this same PDP markup; live gates only the buybox.
    if (form.closest('dialog, quick-add-dialog, quick-add-component')) return;

    const scope = scopeOf(form);
    if (!scope) return;
    if (!sizeFieldsets(scope).length) return; // fail-open: no size option → untouched button
    form.dataset.sizeGate = 'on';

    // A size interaction (including re-clicking the pre-selected chip) confirms the choice.
    // Delegated on the buybox so it survives the picker re-rendering on variant change.
    const confirmChoice = (event) => {
      const fs = event.target instanceof Element ? event.target.closest('fieldset.variant-option--buttons') : null;
      if (fs && isSizeFieldset(fs)) clearPending(form, scope);
    };
    scope.addEventListener('change', confirmChoice);
    scope.addEventListener('click', confirmChoice);

    // A variant change re-renders the buybox and restores the server-rendered label, so the
    // pending state has to be re-asserted until a size is actually chosen.
    const observer = new MutationObserver(() => {
      if (form.dataset.sizeChosen !== 'true') applyPending(form, scope);
    });
    observer.observe(scope, { childList: true, subtree: true });

    applyPending(form, scope);
  };

  const init = () => {
    for (const form of document.querySelectorAll('product-form-component')) {
      try {
        initForm(form);
      } catch (error) {
        console.warn('[select-size-gate] skipped a form', error);
      }
    }
  };

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
